import { useEffect, useState, useCallback } from 'react';
import type { PortfolioReport, CGTReport, PositionSummary, CGTEvent } from '@covered-calls/shared';
import { apiClient, ApiClientError } from '../services/api-client';

/**
 * ReportView component with tabs for Portfolio Performance and CGT Report.
 *
 * Portfolio Performance tab:
 * - Optional date range filter (start date, end date)
 * - Summary cards: Total Premium, Realised Gains/Losses, Unrealised Gains/Losses, Total Commissions, Net Return
 * - Position performance table
 *
 * CGT Report tab:
 * - Financial year selector
 * - Aggregate summary: Gross Gains, Gross Losses, Discount Amount, Net Capital Gain
 * - Table of individual CGT events
 * - Empty state when no disposals
 *
 * Validates: Requirements 10.4, 10.5, 11.1, 11.2, 11.3, 11.4
 */

type ReportTab = 'PORTFOLIO' | 'CGT';

export function ReportView() {
  const [activeTab, setActiveTab] = useState<ReportTab>('PORTFOLIO');

  return (
    <div style={{ width: '100%' }}>
      <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Reports</h2>

      {/* Tab navigation */}
      <nav aria-label="Report type" style={{ marginBottom: '1.5rem' }}>
        <div role="tablist" style={{ display: 'flex', gap: '0.25rem', borderBottom: '2px solid var(--color-border)' }}>
          <button
            role="tab"
            aria-selected={activeTab === 'PORTFOLIO'}
            aria-controls="panel-portfolio"
            id="tab-portfolio"
            onClick={() => setActiveTab('PORTFOLIO')}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderBottom: activeTab === 'PORTFOLIO' ? '2px solid var(--color-brand)' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === 'PORTFOLIO' ? 'var(--color-brand)' : 'var(--color-muted)',
              fontWeight: activeTab === 'PORTFOLIO' ? 600 : 400,
              cursor: 'pointer',
              minHeight: '44px',
              marginBottom: '-2px',
            }}
          >
            Portfolio Performance
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'CGT'}
            aria-controls="panel-cgt"
            id="tab-cgt"
            onClick={() => setActiveTab('CGT')}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderBottom: activeTab === 'CGT' ? '2px solid var(--color-brand)' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === 'CGT' ? 'var(--color-brand)' : 'var(--color-muted)',
              fontWeight: activeTab === 'CGT' ? 600 : 400,
              cursor: 'pointer',
              minHeight: '44px',
              marginBottom: '-2px',
            }}
          >
            CGT Report
          </button>
        </div>
      </nav>

      {/* Tab panels */}
      {activeTab === 'PORTFOLIO' && (
        <div role="tabpanel" id="panel-portfolio" aria-labelledby="tab-portfolio">
          <PortfolioPerformancePanel />
        </div>
      )}
      {activeTab === 'CGT' && (
        <div role="tabpanel" id="panel-cgt" aria-labelledby="tab-cgt">
          <CGTReportPanel />
        </div>
      )}
    </div>
  );
}

// --- Portfolio Performance Panel ---

function PortfolioPerformancePanel() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState<PortfolioReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async (start: string, end: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (start) params.set('startDate', start);
      if (end) params.set('endDate', end);
      const queryString = params.toString();
      const path = `/reports/portfolio${queryString ? `?${queryString}` : ''}`;
      const data = await apiClient.get<PortfolioReport>(path);
      setReport(data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to load portfolio report. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(startDate, endDate);
  }, []); // Load on mount with no filters

  const handleApplyFilter = () => {
    fetchReport(startDate, endDate);
  };

  return (
    <div>
      {/* Date range filter */}
      <fieldset
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: '4px',
          padding: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <legend style={{ fontWeight: 600, padding: '0 0.5rem' }}>Date Range Filter</legend>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label htmlFor="start-date" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', minHeight: '38px' }}
            />
          </div>
          <div>
            <label htmlFor="end-date" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', minHeight: '38px' }}
            />
          </div>
          <button
            onClick={handleApplyFilter}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--color-brand)',
              color: 'var(--color-on-brand)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              minHeight: '38px',
              fontWeight: 500,
            }}
          >
            Apply Filter
          </button>
        </div>
      </fieldset>

      {/* Loading state */}
      {isLoading && (
        <div aria-live="polite" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
          Loading portfolio report...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div role="alert" style={{ padding: '1rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px', marginBottom: '1rem' }}>
          <p style={{ margin: 0 }}>{error}</p>
          <button
            onClick={() => fetchReport(startDate, endDate)}
            style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', minHeight: '44px' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Report content */}
      {!isLoading && !error && report && (
        <>
          {/* Summary cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem',
            }}
          >
            <SummaryCard label="Total Premium Income" value={formatCurrency(report.totalPremiumIncome)} />
            <SummaryCard label="Realised Gains" value={formatCurrency(report.totalRealisedGains)} color="var(--color-pnl-positive)" />
            <SummaryCard label="Realised Losses" value={formatCurrency(report.totalRealisedLosses)} color="var(--color-pnl-negative)" />
            <SummaryCard
              label="Realised Return %"
              value={formatPercent(report.realisedReturnPercentage)}
              color={report.realisedReturnPercentage >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)'}
            />
            <SummaryCard label="Unrealised Gains" value={formatCurrency(report.totalUnrealisedGains)} color="var(--color-pnl-positive)" />
            <SummaryCard label="Unrealised Losses" value={formatCurrency(report.totalUnrealisedLosses)} color="var(--color-pnl-negative)" />
            <SummaryCard
              label="Unrealised Return %"
              value={formatPercent(report.unrealisedReturnPercentage)}
              color={report.unrealisedReturnPercentage >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)'}
            />
            <SummaryCard label="Total Commissions" value={formatCurrency(report.totalCommissionsPaid)} />
            <SummaryCard label="Dividend Income" value={formatCurrency(report.totalDividendIncome)} color="var(--color-pnl-positive)" />
            <SummaryCard label="Protective Put Cost" value={formatCurrency(report.totalProtectivePutCost)} color="var(--color-pnl-negative)" />
            <SummaryCard
              label="Net Return"
              value={formatCurrency(report.netPortfolioReturn)}
              color={report.netPortfolioReturn >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)'}
            />
          </div>

          {/* Position performance table */}
          <h3 style={{ marginBottom: '0.75rem' }}>Position Performance</h3>
          {report.positions.length === 0 ? (
            <p style={{ color: 'var(--color-muted)' }}>No positions found for the selected date range.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }} aria-label="Position performance">
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Ticker</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Premium</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Stock Gain/Loss</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Commissions</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Net P&L</th>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.positions.map((pos) => (
                    <PositionRow key={pos.positionId} position={pos} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- CGT Report Panel ---

function CGTReportPanel() {
  const currentYear = new Date().getFullYear();
  // Financial year: July to June. FY 2024-25 is represented as 2025.
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [report, setReport] = useState<CGTReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate year options (current FY and past 5 years)
  const yearOptions: number[] = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  const fetchReport = useCallback(async (year: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<CGTReport>(`/reports/cgt/${year}`);
      setReport(data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to load CGT report. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(selectedYear);
  }, [selectedYear, fetchReport]);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(Number(e.target.value));
  };

  return (
    <div>
      {/* Financial year selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="financial-year" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
          Financial Year
        </label>
        <select
          id="financial-year"
          value={selectedYear}
          onChange={handleYearChange}
          style={{
            padding: '0.5rem',
            border: '1px solid var(--color-input-border)',
            borderRadius: '4px',
            minHeight: '38px',
            minWidth: '200px',
          }}
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              FY {year - 1}-{String(year).slice(-2)} (Jul {year - 1} - Jun {year})
            </option>
          ))}
        </select>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div aria-live="polite" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
          Loading CGT report...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div role="alert" style={{ padding: '1rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px', marginBottom: '1rem' }}>
          <p style={{ margin: 0 }}>{error}</p>
          <button
            onClick={() => fetchReport(selectedYear)}
            style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', minHeight: '44px' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Report content */}
      {!isLoading && !error && report && (
        <>
          {/* Aggregate summary */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem',
            }}
          >
            <SummaryCard label="Gross Capital Gains" value={formatCurrency(report.aggregateGrossGains)} color="var(--color-pnl-positive)" />
            <SummaryCard label="Gross Capital Losses" value={formatCurrency(report.aggregateGrossLosses)} color="var(--color-pnl-negative)" />
            <SummaryCard label="CGT Discount Applied" value={formatCurrency(report.aggregateDiscountAmount)} />
            <SummaryCard
              label="Net Capital Gain"
              value={formatCurrency(report.aggregateNetCapitalGain)}
              color={report.aggregateNetCapitalGain >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)'}
            />
          </div>

          {/* CGT events table or empty state */}
          <h3 style={{ marginBottom: '0.75rem' }}>Disposal Events</h3>
          {report.events.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)', backgroundColor: 'var(--color-surface-2)', borderRadius: '4px' }}>
              <p style={{ margin: 0 }}>No disposals recorded in this financial year.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }} aria-label="CGT events">
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Ticker</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Purchase Price</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Sale Price</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Holding (days)</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Commissions</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Gross Gain</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Discount</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Net Gain</th>
                  </tr>
                </thead>
                <tbody>
                  {report.events.map((event, idx) => (
                    <CGTEventRow key={`${event.positionId}-${idx}`} event={event} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Shared Sub-Components ---

function SummaryCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div
      style={{
        padding: '1rem',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        backgroundColor: 'var(--color-surface)',
      }}
    >
      <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: color || 'var(--color-text)' }}>
        {value}
      </div>
    </div>
  );
}

function PositionRow({ position }: { position: PositionSummary }) {
  const pnlColor = position.netProfitLoss > 0 ? 'var(--color-pnl-positive)' : position.netProfitLoss < 0 ? 'var(--color-pnl-negative)' : 'var(--color-muted)';

  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
      <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>{position.ticker}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(position.premiumReceived)}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: position.stockGainLoss >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)' }}>
        {formatCurrency(position.stockGainLoss)}
      </td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(position.commissionsPaid)}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 600, color: pnlColor }}>
        {formatCurrency(position.netProfitLoss)}
      </td>
      <td style={{ padding: '0.75rem 0.5rem' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '0.125rem 0.5rem',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 600,
            backgroundColor: position.status === 'OPEN' ? 'var(--color-chip-success-bg)' : 'var(--color-surface-2)',
            color: position.status === 'OPEN' ? 'var(--color-chip-success-text)' : 'var(--color-muted)',
          }}
        >
          {position.status}
        </span>
      </td>
    </tr>
  );
}

function CGTEventRow({ event }: { event: CGTEvent }) {
  const totalCommissions = event.buyCommission + event.sellCommission;
  const gainColor = event.grossCapitalGain >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)';
  const netColor = event.netCapitalGain >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)';

  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
      <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>{event.ticker}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(event.purchasePrice)}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(event.salePrice)}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{event.quantity}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{event.holdingPeriodDays}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(totalCommissions)}</td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: gainColor }}>
        {formatCurrency(event.grossCapitalGain)}
      </td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
        {event.discountApplicable ? formatCurrency(event.discountAmount) : 'N/A'}
      </td>
      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 600, color: netColor }}>
        {formatCurrency(event.netCapitalGain)}
      </td>
    </tr>
  );
}

// --- Utility ---

function formatCurrency(value: number): string {
  const prefix = value < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(value).toFixed(2)}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}
